import { createFileRoute, Link } from "@tanstack/react-router";
import portraitImg from "@/assets/dj-mariage-portrait.jpg";
import { useReveal } from "@/hooks/use-reveal";
import { PageHero } from "@/components/page-hero";
import { metaDesc } from "@/lib/seo-description";

const URL = "https://dj-val.fr/en/wedding-dj-provence";

const FAQ = [
  {
    q: "Do you speak English for international weddings?",
    a: "Yes. We coordinate every detail of your wedding day in English, from the first meeting to the final track. We are used to working with bilingual masters of ceremony, English-speaking planners and international guests in Gordes, the Luberon, Avignon and Saint-Rémy. Free quote within 24 hours.",
  },
  {
    q: "How much does a wedding DJ cost in Provence?",
    a: "Wedding DJ packages start at 690 € for a ceremony + reception, and from 1,490 € for a full destination wedding setup (ceremony sound, cocktail, dinner ambient, full evening with lighting design and effects). All quotes include sound system, basic lighting, setup, takedown and travel within 80 km of Cavaillon.",
  },
  {
    q: "Which venues do you work with in the Luberon?",
    a: "We regularly play at hilltop bastides and wine estates in the Luberon (Gordes, Ménerbes, Bonnieux, Lourmarin) and the Alpilles (Saint-Rémy-de-Provence, Eygalières). We always do a venue scout before the wedding to check sound, electricity and acoustic constraints.",
  },
  {
    q: "What does the package include?",
    a: "Pioneer / RCF professional sound system, LED uplighting and dancefloor lights, wireless microphones for the ceremony, backup gear, setup and takedown, SACEM declaration and full liability insurance (Hiscox).",
  },
  {
    q: "When should we book?",
    a: "For a destination wedding in Provence, we recommend booking 8 to 14 months in advance, especially for dates between May and September. Peak Saturdays in June and September are often booked a full year ahead.",
  },
];

export const Route = createFileRoute("/en/wedding-dj-provence")({
  head: () => ({
    meta: [
      { title: "Wedding DJ in Provence — Gordes, Luberon, Avignon | DJ Val" },
      {
        name: "description",
        content:
          "Professional wedding DJ in Provence: ceremony sound, lighting design and an unforgettable dancefloor in Gordes, the Luberon, Avignon and Saint-Rémy.",
      },
      { property: "og:title", content: "Wedding DJ in Provence — DJ Val" },
      { property: "og:description", content: metaDesc("Bilingual wedding DJ for destination weddings in the Luberon, Gordes & Avignon.") },
      { property: "og:url", content: URL },
      { property: "og:locale", content: "en_GB" },
      { property: "og:locale:alternate", content: "fr_FR" },
      { property: "og:image", content: portraitImg },
    ],
    links: [
      { rel: "canonical", href: URL },
      { rel: "alternate", hrefLang: "en", href: URL },
      { rel: "alternate", hrefLang: "fr-FR", href: "https://dj-val.fr/" },
      { rel: "alternate", hrefLang: "x-default", href: "https://dj-val.fr/" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Wedding DJ in Provence",
          serviceType: "Wedding DJ & Sound for destination weddings",
          provider: { "@id": "https://dj-val.fr/#business" },
          areaServed: [
            { "@type": "AdministrativeArea", name: "Provence" },
            { "@type": "AdministrativeArea", name: "Vaucluse" },
            { "@type": "City", name: "Cavaillon" },
            { "@type": "City", name: "Gordes" },
            { "@type": "City", name: "Avignon" },
            { "@type": "City", name: "Saint-Rémy-de-Provence" },
            { "@type": "AdministrativeArea", name: "Luberon" },
          ],
          offers: {
            "@type": "AggregateOffer",
            priceCurrency: "EUR",
            lowPrice: "690",
            highPrice: "2900",
            availability: "https://schema.org/InStock",
          },
          inLanguage: ["en", "fr"],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: Page,
});

function Page() {
  useReveal();
  return (
    <div className="bg-background text-muted-foreground" lang="en">
      <PageHero
        image={portraitImg}
        eyebrow="Destination wedding · Provence, France"
        title={<>Wedding DJ in <span className="italic">Provence</span></>}
        subtitle="Bilingual wedding DJ based in Cavaillon, 30 minutes from Avignon. 15+ years animating destination weddings across the Luberon, the Alpilles and the Côtes du Rhône."
        primary={{ label: "Free quote in 24 h", to: "/contactez-nous" }}
        secondary={{ label: "+33 7 67 90 86 67", href: "tel:+33767908667" }}
      />


      {/* WHY VAL */}
      <section className="py-20 px-6 max-w-5xl mx-auto">
        <div className="grid md:grid-cols-3 gap-8">
          {[
            { n: "01", t: "Bilingual coordination", d: "Every detail discussed in fluent English. We work with your planner, your MC and your venue contact." },
            { n: "02", t: "Professional gear", d: "Pioneer CDJ-2000NXS2, RCF HDL line array, Shure ULXD microphones, Chauvet lighting. Full backup setup." },
            { n: "03", t: "Local expertise", d: "We know the constraints of Luberon bastides: noise limits, generator power, outdoor ceremonies, late-night ordinances." },
          ].map((c, i) => (
            <div key={c.n} className="sr border border-foreground/10 p-8" data-reveal style={{ transitionDelay: `${i * 80}ms` }}>
              <p className="font-serif-display italic text-[var(--gold)] text-3xl">{c.n}</p>
              <h3 className="font-serif-display text-xl text-foreground mt-3">{c.t}</h3>
              <span className="gold-rule !w-8 my-4" />
              <p className="text-sm font-light leading-relaxed">{c.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* VENUES */}
      <section className="py-20 px-6 bg-card border-y border-foreground/10">
        <div className="max-w-4xl mx-auto sr" data-reveal>
          <p className="eyebrow text-foreground">Venues we know</p>
          <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground mt-4">From hilltop bastides to private estates</h2>
          <span className="gold-rule !w-12 my-6" />
          <p className="text-sm font-light leading-relaxed">
            Hilltop villages of <strong className="text-foreground">Gordes</strong>,{" "}
            <strong className="text-foreground">Ménerbes</strong> and <strong className="text-foreground">Bonnieux</strong>;
            wine estates of the <strong className="text-foreground">Côtes du Luberon</strong>; bastides of the{" "}
            <strong className="text-foreground">Alpilles</strong> around Saint-Rémy-de-Provence; Renaissance courtyards
            in <strong className="text-foreground">Avignon</strong>. We do a venue scout before every wedding to plan
            sound, lighting and electrical setup precisely.
          </p>
          <p className="text-sm font-light leading-relaxed mt-4">
            We coordinate seamlessly with the leading wedding planners and photographers in the region, and we are
            happy to liaise with your videographer for sound recording.
          </p>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="py-20 px-6 max-w-5xl mx-auto">
        <div className="text-center mb-12 sr" data-reveal>
          <p className="eyebrow text-foreground">Included in every quote</p>
          <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground mt-4">Full destination-wedding package</h2>
          <span className="gold-rule mx-auto !w-12 my-6" />
        </div>
        <div className="grid sm:grid-cols-2 gap-x-12 gap-y-3 text-sm font-light max-w-2xl mx-auto">
          {[
            "Ceremony sound (wireless mics, music)",
            "Cocktail hour ambient music",
            "Dinner background sets",
            "Full evening DJ set (up to 6 h)",
            "Pioneer DJ setup + RCF speakers",
            "LED uplighting & dancefloor lights",
            "Backup gear on site",
            "Setup, sound check & takedown",
            "Travel within 80 km of Cavaillon",
            "SACEM declaration + insurance",
          ].map((i) => (
            <p key={i} className="flex gap-2"><span className="text-[var(--gold)]">✓</span>{i}</p>
          ))}
        </div>
      </section>

      {/* PRICING */}
      <section className="py-20 px-6 bg-card border-y border-foreground/10">
        <div className="max-w-4xl mx-auto sr" data-reveal>
          <p className="eyebrow text-foreground text-center">Transparent pricing</p>
          <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground text-center mt-4">From 690 € to 2,900 €</h2>
          <span className="gold-rule mx-auto !w-12 my-6" />
          <div className="grid md:grid-cols-3 gap-4 mt-10">
            {[
              { p: "Essential", price: "690 €", d: "Reception only · 5 h · sound + basic lights" },
              { p: "Signature", price: "1,490 €", d: "Ceremony + cocktail + evening · 8 h · full lighting", hl: true },
              { p: "Prestige", price: "2,290 €", d: "Full day · multi-zone sound · effects · cold sparks" },
            ].map((t) => (
              <div key={t.p} className={`border p-6 ${t.hl ? "border-[var(--gold)] bg-background" : "border-foreground/10"}`}>
                <p className="text-[10px] uppercase tracking-[0.3em] text-foreground/85">{t.p}</p>
                <p className="font-serif-display italic text-[var(--gold)] text-3xl mt-2">{t.price}</p>
                <p className="text-xs font-light mt-3">{t.d}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-xs text-foreground/85 mt-6">
            Free, no-obligation quote within 24 hours.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12 sr" data-reveal>
            <p className="eyebrow text-foreground">Frequently asked</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground mt-4">Good to know</h2>
            <span className="gold-rule mx-auto !w-12 my-6" />
          </div>
          <div className="space-y-3">
            {FAQ.map((f, i) => (
              <details key={i} className="group border border-foreground/10 p-5 sr" data-reveal style={{ transitionDelay: `${i * 50}ms` }}>
                <summary className="cursor-pointer font-serif-display text-lg text-foreground">{f.q}</summary>
                <p className="mt-3 text-sm font-light leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 text-center">
        <div className="max-w-2xl mx-auto sr" data-reveal>
          <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground">
            Let's talk about your <span className="italic text-[var(--gold)]">Provence wedding</span>
          </h2>
          <span className="gold-rule mx-auto !w-12 my-8" />
          <p className="text-sm font-light leading-relaxed mb-8">
            Tell us your date, venue and guest count — you'll receive a detailed quote within 24 hours.
          </p>
          <Link to="/contactez-nous" className="inline-block bg-[var(--gold)] text-foreground px-10 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] hover:brightness-110 transition-all">
            Request a free quote
          </Link>
          <p className="text-xs text-foreground/85 mt-8">
            Prefer French? <Link to="/dj-mariage" className="text-[var(--gold)] hover:underline">Voir la version française →</Link>
          </p>
        </div>
      </section>
    </div>
  );
}
